<template>
	<div class="content content-500 column centering flexible">
		<div v-if="loading" class="full-width placeholder-container">
			<header class="row">
				<h1-placeholder class="rigid" :text="'Setup your Address'"></h1-placeholder>
			</header>
			<div class="panel relative">
				<spinner></spinner>
			</div>
		</div>
		<div v-else-if="promoDetails" class="full-width">
			<progress-tracker :current-step="navigation.current.name" :is-complete="navigation.isComplete" :steps="progressSteps"></progress-tracker>
			<v-component v-if="promoDetails" v-on:navigation="navigateTo" :promo-code="code" :promo-details="promoDetails" :navigation="navigation" :is="navigation.current.view"></v-component>
		</div>
		<div v-else class="row separated-extra centering invalid-promo">
			<div class="column panel height-auto flexible">
				<i class="rigid fas fa-question-circle jumbo error"></i>
				<h2 class="error no-margin-bottom">Promotion not found.</h2>
				<p class="no-margin-bottom">If you believe this to be a mistake, please <a href="mailto:sales@support.tekcloud.com?subject=Promotion%20Not%20Found"><strong>contact support</strong></a>.</p>
			</div>
		</div>
	</div>
</template>

<script>
import Address from './Address';
import Spinner from '@/components/SpinnerNew';
import H1Placeholder from '@/components/H1Placeholder';
import ProgressTracker from '@/components/ProgressTracker';
import PaymentMethod from './PaymentMethod';
import Review from './Review';
import Confirmation from './Confirmation';
import { mapGetters } from 'vuex';
import ENV_VARS from '@/modules/env-vars.js';
import SubmitButton from '@/components/SubmitButton';

export default {
	components:{
		Address,
		PaymentMethod,
		SubmitButton,
		Review,
		Confirmation,
		Spinner,
		H1Placeholder,
		ProgressTracker
	},
	props: {
		code: String,
		promoUrl: String
	},
	data(){
		return {
			steps: [],
			currentStep: null,
			currentStepIndex: 0,
			navigation: null,
			wasCurrent: null,
			product: null
		};
	},
	computed:{
		...mapGetters([
			'address',
			'userStatus',
			'account',
			'accountStatus',
			'hasUsAddress',
			'hasNonUsAddress',
		]),
		progressSteps() {
			let steps = this.steps.filter(step => step.showProgress).map(step => step.name);
			return [...new Set(steps)];
		},
		loading() {
			return this.navigation === null;
		},
		PROMO_URL() {
			return this.promoUrl;
		},
		promoDetails() {
			const products = {
					'2-series': {
						collectPayment: true,
						items: [
							{
								name: 'TekDrive Starter',
								cost: '$299.88 / year',
								offer: '6 months free',
								details: {
									storageSizeLimit: {
										value: 50000000000,
										display: '50 GB of storage'
									}
								}
							},
							{
								name: 'TekScope Ultimate',
								cost: '$504 / year',
								offer: '6 months free'
							}
						],
					},
					'tbs1k': {
						collectPayment: true,
						items: [
							{
								name: 'TekDrive Starter',
								cost: '$299.88 / year',
								offer: '12 months free',
								details: {
									storageSizeLimit: {
										value: 50000000000,
										display: '50 GB of storage'
									}
								}
							},
							{
								name: 'TekScope Starter',
								cost: '$201 / year',
								offer: '12 months free',
							}
						],
					},
					'tekdriveportal': {
						collectPayment: false,
						items: [
							{
								name: 'TekDrive Portal User Plan 200',
								cost: '',
								offer: 'Free forever',
								details: {
									storageSizeLimit: {
										value: 200000000,
										display: '200 MB of storage'
									}
								}

							}
						]
					}
				},
				product = this.product && this.product.toLowerCase();

			return products[product];
		}
	},
	methods:{
		initilize() {
			let product = '';

			try {
				// decode base64 encoded string to get promo product name
				product = Buffer.from(this.code, 'base64').toString().split(':')[0];

			} catch(e) {
				// set to empty string and let fail later
				product = '';
			}

			// use marketing name to ensure return url is correct
			this.product = (product === 'lexington') ? '2-series' : product;
			console.log(this.promoDetails);
			this.getNavigation();
		},
		navigateTo(stepIndex) {

			const current = this.steps[stepIndex];

			// if going to PaymentMethod from Review
			if (current.view === 'PaymentMethod' && this.currentViewWas === 'Review') {
				current.showProgress = true;
			}

			this.navigation = {
				current: current,
				next: this.steps[stepIndex + 1],
				previous: this.steps[stepIndex - 1],
				start: this.steps[0],
				currentIndex: stepIndex,
				isComplete: stepIndex === this.steps.length - 1
			};

			this.currentViewWas = this.navigation.current.view;
		},
		reduceNavigationSteps() {
			const stepIndex = this.navigation.currentIndex -= 1;
			const current = this.steps[stepIndex];

			this.navigation = {
				current: current,
				next: this.steps[stepIndex + 1],
				previous: this.steps[stepIndex - 1],
				start: this.steps[0],
				currentIndex: stepIndex,
				isComplete: stepIndex === this.steps.length - 1
			};
		},
		initNavigation(steps, startIndex) {
			this.steps = steps;
			this.navigateTo(startIndex);
		},
		getNavigation() {
			// promo doesnt require an address or credit card
			if (this.promoDetails && !this.promoDetails.collectPayment) {
				this.initNavigation([
					{
						name: 'Promotion',
						view: this.PROMO_URL,
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Review',
						view: 'Review',
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Confirmation',
						view: 'Confirmation',
						showProgress: false,
						canNavigateTo: false
					}
				], 1);

			}
			// user has an address and payment method
			else if (this.address && this.account.paymentMethod) {
				this.initNavigation([
					{
						name: 'Promotion',
						view: this.PROMO_URL,
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Setup',
						view: 'PaymentMethod',
						showProgress: false,
						canNavigateTo: true
					},
					{
						name: 'Review',
						view: 'Review',
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Confirmation',
						view: 'Confirmation',
						showProgress: false,
						canNavigateTo: false
					}
				], 2);

			// user has an address (US) but needs payment method
			} else if (this.hasUsAddress) {
				this.initNavigation([
					{
						name: 'Promotion',
						view: this.PROMO_URL,
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Setup',
						view: 'PaymentMethod',
						showProgress: true,
						canNavigateTo: true
					},
					{
						name: 'Review',
						view: 'Review',
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Confirmation',
						view: 'Confirmation',
						showProgress: false,
						canNavigateTo: false
					}
				], 1);

			// user has an address (non-US)
			} else if (this.hasNonUsAddress) {
				this.initNavigation([
					{
						name: 'Promotion',
						view: this.PROMO_URL,
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Review',
						view: 'Review',
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Confirmation',
						view: 'Confirmation',
						showProgress: false,
						canNavigateTo: false
					}
				], 1);

			// user doesn't have an address
			} else {
				this.initNavigation([
					{
						name: 'Promotion',
						view: this.PROMO_URL,
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Setup',
						view: 'Address',
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Setup',
						view: 'PaymentMethod',
						showProgress: true,
						canNavigateTo: true
					},
					{
						name: 'Review',
						view: 'Review',
						showProgress: true,
						canNavigateTo: false
					},
					{
						name: 'Confirmation',
						view: 'Confirmation',
						showProgress: false,
						canNavigateTo: false
					}
				], 1);
			}
		}
	},
	watch: {
		userStatus: {
			handler(val, old) {
				if (val.success) {
					this.initilize();
				}
			},
			immediate: true
		},
		hasNonUsAddress(val) {
			if (val) {
				// remove payment step from int't addresses
				let paymentStepIndex = this.steps.findIndex(step => step.view === 'PaymentMethod');
				if (paymentStepIndex > -1) {
					this.steps.splice(paymentStepIndex, 1);
					this.reduceNavigationSteps();
				}

			}
		},
	},
};
</script>

<style scoped>

.breadcrumbs {
	margin: 1em auto;
}
.breadcrumbs small {
	margin: 1em;
}
.panel {
	background: white;
	border-radius: 6px;
	padding: 2rem;
	height: 200px;
	border: 2px solid #dfdfdf;
	width: 100%;
	margin: auto;
	box-sizing: border-box;
}
.dark .panel {
	background: #1a1a1a;
	border: 2px solid #333;
}
.panel.height-auto {
	height: auto;
}
.placeholder-container > header {
	margin-top: 80px;
}
i.error, h2.error {
	color: #D7000B;
}
.dark i.error, .dark h2.error {
	color: #ec5b7a;
}
.no-margin-bottom {
	margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// ./src/views/promotions/Promotions.vue